import React, { useEffect, useState, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import {
  FiArrowUpRight,
  FiZap,
  FiLayers,
  FiCpu,
  FiNavigation,
  FiGrid,
  FiSend,
  FiClipboard,
  FiCheckCircle,
  FiClock,
  FiMoon,
  FiAlertTriangle,
  FiAlertOctagon,
  FiChevronDown
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { getServicesForDepartment, normalizeDepartment, normalizeServiceRequestActivity } from '../utils/departmentMapping';
import { Link, useNavigate } from 'react-router-dom';
import { formatDateTime, parseServerDate } from '../utils/dateUtils';
import { renderFormattedText } from '../utils/formatUtils';
import dashboardHeroImg from '../assets/dashboard_hero.jpg';

/* ------------------------------------------------------------------ */
/*  CardActions (Matches elyfleet exactly)                            */
/* ------------------------------------------------------------------ */
function CardActions({ onClick, title = "Open tab" }) {
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      title={title}
      className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 cursor-pointer"
    >
      <FiArrowUpRight className="h-4 w-4" />
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  ActivityBarChart (Glowing Horizon: #4272FF + #42EAFF)             */
/* ------------------------------------------------------------------ */
function ActivityBarChart({ series, onBarClick }) {
  const [hovered, setHovered] = useState(null);
  const maxVal = Math.max(1, ...series.map((s) => Number(s.value || 0)));
  const hoveredRow = hovered != null ? series[hovered] : null;

  return (
    <div>
      <div className="flex items-end gap-[8px] pt-4" style={{ height: 110 }}>
        {series.map((s, i) => {
          const val = Number(s.value || 0);
          const pct = Math.max((val / maxVal) * 100, 6);
          const isHighlight = i === series.length - 1;
          const isHovered = hovered === i;
          return (
            <div key={s.label} className="flex flex-1 flex-col items-center gap-1 min-w-0">
              <div className="flex w-full items-end justify-center" style={{ height: 80 }}>
                <div
                  className={`w-[22px] shrink-0 cursor-pointer rounded-lg transition-all duration-200 ${
                    isHovered ? 'brightness-95 scale-105' : ''
                  }`}
                  style={{
                    height: `${pct}%`,
                    backgroundColor: isHighlight ? '#42EAFF' : '#4272FF',
                    boxShadow: isHighlight ? '0 2px 10px rgba(66, 234, 255, 0.45)' : 'none',
                  }}
                  title={`${s.label}: ${val} req`}
                  onClick={() => {
                    setHovered((prev) => (prev === i ? null : i));
                    if (onBarClick) onBarClick(s);
                  }}
                  onTouchStart={() => setHovered((prev) => (prev === i ? null : i))}
                  onMouseEnter={() => setHovered(i)}
                  onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
                  role="img"
                  tabIndex={0}
                />
              </div>
              <div className="flex flex-col items-center leading-tight">
                <span
                  className="text-[10px] font-semibold"
                  style={{ color: isHighlight ? '#0284c7' : '#9ca3af' }}
                >
                  {s.label}
                </span>
                <span
                  className={`text-[9px] font-bold transition-colors ${
                    isHovered
                      ? 'text-gray-900 font-extrabold'
                      : isHighlight
                        ? 'text-sky-700'
                        : 'text-gray-500'
                  }`}
                >
                  {val}
                </span>
              </div>
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 min-h-4 text-center text-[10px] font-medium text-gray-500">
        {hoveredRow ? (
          <span className="font-semibold text-gray-800">
            {hoveredRow.label}: {hoveredRow.value} requests
          </span>
        ) : (
          'Tap / hover a bar to view daily req'
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  StatusDonutPie (All 5 Live Statuses with Custom Radio Cards)      */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/*  StatusDonutPie (All 5 Live Statuses with Custom Radio Cards)      */
/* ------------------------------------------------------------------ */
function StatusDonutPie({
  pending = 0,
  completed = 0,
  dnd = 0,
  critical = 0,
  dispute = 0,
  cancelled = 0,
  total = 0,
  onNavigate
}) {
  const statuses = [
    {
      id: 'completed',
      label: 'Completed',
      count: completed,
      color: '#42EAFF',
      bg: 'rgba(66, 234, 255, 0.12)',
      border: 'rgba(66, 234, 255, 0.35)',
      text: '#0369a1',
      url: '/requests?status=completed'
    },
    {
      id: 'pending',
      label: 'Pending',
      count: pending,
      color: '#FFB343',
      bg: 'rgba(255, 179, 67, 0.12)',
      border: 'rgba(255, 179, 67, 0.35)',
      text: '#b45309',
      url: '/requests?status=pending'
    },
    {
      id: 'dnd',
      label: 'DND',
      count: dnd,
      color: '#4272FF',
      bg: 'rgba(66, 114, 255, 0.1)',
      border: 'rgba(66, 114, 255, 0.3)',
      text: '#284fd6',
      url: '/requests?status=dnd'
    },
    {
      id: 'critical',
      label: 'Critical Fix',
      count: critical,
      color: '#FF7E42',
      bg: 'rgba(255, 126, 66, 0.12)',
      border: 'rgba(255, 126, 66, 0.35)',
      text: '#c2410c',
      url: '/critical-fixes'
    },
    {
      id: 'dispute',
      label: 'Service Dispute',
      count: dispute,
      color: '#FF7E42',
      bg: 'rgba(255, 126, 66, 0.1)',
      border: 'rgba(255, 126, 66, 0.3)',
      text: '#9a3412',
      url: '/requests?view=dispute'
    }
  ];

  if (cancelled > 0) {
    statuses.push({
      id: 'cancelled',
      label: 'Cancelled',
      count: cancelled,
      color: '#f43f5e',
      bg: 'rgba(244, 63, 94, 0.1)',
      border: 'rgba(244, 63, 94, 0.3)',
      text: '#be123c',
      url: '/requests?view=cancelled'
    });
  }

  const calculatedTotal = statuses.reduce((sum, s) => sum + s.count, 0);
  const grandTotal = total || calculatedTotal;

  // SVG Donut calculation
  const size = 140;
  const strokeWidth = 14;
  const radius = (size - strokeWidth) / 2; // 63
  const circumference = 2 * Math.PI * radius; // ~395.84

  const activeSlices = statuses.filter((s) => s.count > 0);
  let cumulativePercent = 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 w-full items-center py-0.5">
      
      {/* Left 50%: Attractive Centered Donut Pie Chart */}
      <div className="w-full flex items-center justify-center">
        <div
          className="relative shrink-0 cursor-pointer transition-all duration-300 hover:scale-105 group"
          onClick={() => onNavigate?.('/requests?status=all')}
          title="View All Requests"
        >
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="transform -rotate-90 filter drop-shadow-xs"
          >
            {/* Background track */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="transparent"
              stroke="#f1f5f9"
              strokeWidth={strokeWidth}
            />

            {/* Segmented Slices with clean gaps */}
            {calculatedTotal > 0 &&
              activeSlices.map((s) => {
                const percent = s.count / calculatedTotal;
                const gap = activeSlices.length > 1 ? 2.5 : 0;
                const sliceLength = Math.max(0, percent * circumference - gap);
                const strokeDasharray = `${sliceLength} ${circumference - sliceLength}`;
                const strokeDashoffset = -cumulativePercent * circumference;
                cumulativePercent += percent;

                return (
                  <circle
                    key={s.id}
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="transparent"
                    stroke={s.color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={strokeDasharray}
                    strokeDashoffset={strokeDashoffset}
                    className="transition-all duration-300 hover:opacity-90"
                  />
                );
              })}
          </svg>

          {/* Center Total Counter */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-3xl font-black text-gray-900 tracking-tight leading-none">
              {grandTotal}
            </span>
            <span className="text-[10px] font-extrabold text-gray-400 uppercase tracking-wider mt-1">
              TOTAL REQS
            </span>
          </div>
        </div>
      </div>

      {/* Right 50%: 100% Symmetrical 5-Status Radio List */}
      <div className="w-full flex flex-col gap-1.5">
        {statuses.map((s) => {
          const hasData = s.count > 0;
          return (
            <div
              key={s.id}
              onClick={(e) => {
                e.stopPropagation();
                onNavigate?.(s.url);
              }}
              className={`flex items-center justify-between px-3 py-1.5 rounded-xl border transition-all duration-200 cursor-pointer hover:translate-x-1 hover:shadow-xs active:scale-[0.99] group ${
                hasData ? '' : 'opacity-70 bg-gray-50/50 border-gray-100'
              }`}
              style={{
                backgroundColor: hasData ? s.bg : undefined,
                borderColor: hasData ? s.border : undefined
              }}
              title={`View ${s.label} (${s.count})`}
            >
              {/* Radio Button & Label */}
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className="h-3.5 w-3.5 rounded-full border-2 flex items-center justify-center shrink-0 transition-transform duration-200 group-hover:scale-110"
                  style={{ borderColor: hasData ? s.color : '#cbd5e1' }}
                >
                  <div
                    className="h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: hasData ? s.color : '#cbd5e1' }}
                  />
                </div>
                <span className="text-xs font-bold text-gray-800 truncate">{s.label}</span>
              </div>

              {/* Number Count & Arrow */}
              <div className="flex items-center gap-2 shrink-0">
                <span
                  className="text-sm sm:text-base font-extrabold tracking-tight font-mono"
                  style={{ color: hasData ? s.text : '#94a3b8' }}
                >
                  {s.count}
                </span>
                <span className="text-xs font-bold text-gray-400 group-hover:translate-x-0.5 transition-transform">
                  →
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ResolutionSpeedLineChart (7-Day Avg Turnaround Speed in Min)      */
/* ------------------------------------------------------------------ */
function ResolutionSpeedLineChart({ series }) {
  const [hovered, setHovered] = useState(null);
  if (!series || series.length < 2) return null;
  const maxVal = Math.max(...series.map((s) => Number(s.value || 0)), 1);
  const max = Math.ceil(maxVal * 1.3);
  const W = 300;
  const H = 85;
  const pad = 12;

  const pts = series.map((s, i) => {
    const val = Math.max(0, Number(s.value || 0));
    return {
      x: pad + (i / Math.max(1, series.length - 1)) * (W - pad * 2),
      y: H - pad - (val / max) * (H - pad * 2),
    };
  });

  const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ');
  const last = pts[pts.length - 1];
  const first = pts[0];
  const area = `${line} L ${last.x},${H - pad} L ${first.x},${H - pad} Z`;
  const hoveredPoint = hovered != null ? pts[hovered] : null;
  const hoveredRow = hovered != null ? series[hovered] : null;

  return (
    <div className="w-full my-2">
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="h-[82px] w-full" preserveAspectRatio="none">
          <defs>
            <linearGradient id="speedGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FF7E42" stopOpacity="0.25" />
              <stop offset="100%" stopColor="#FF7E42" stopOpacity="0.01" />
            </linearGradient>
          </defs>
          {[0, 0.5, 1].map((t) => (
            <line
              key={t}
              x1={pad}
              y1={pad + t * (H - pad * 2)}
              x2={W - pad}
              y2={pad + t * (H - pad * 2)}
              stroke="#f3f4f6"
              strokeWidth="1"
            />
          ))}
          <path d={area} fill="url(#speedGrad)" />
          <path
            d={line}
            fill="none"
            stroke="#FF7E42"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {hoveredPoint && (
            <line
              x1={hoveredPoint.x}
              x2={hoveredPoint.x}
              y1={pad}
              y2={H - pad}
              stroke="#FF7E42"
              strokeWidth="1.5"
              strokeDasharray="3 3"
            />
          )}
          {pts.map((p, i) => (
            <g key={i}>
              <circle
                cx={p.x}
                cy={p.y}
                r={hovered === i ? '4.5' : '3'}
                fill={i === pts.length - 1 ? '#FFB343' : '#FF7E42'}
                stroke="white"
                strokeWidth="2"
              />
              <circle
                cx={p.x}
                cy={p.y}
                r="12"
                fill="transparent"
                onMouseEnter={() => setHovered(i)}
                onMouseLeave={() => setHovered((h) => (h === i ? null : h))}
              />
            </g>
          ))}
        </svg>
        {hoveredPoint && hoveredRow && (
          <div
            className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-md border border-gray-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-gray-800 shadow-sm"
            style={{
              left: `${(hoveredPoint.x / W) * 100}%`,
              top: `${(hoveredPoint.y / H) * 100}%`,
            }}
          >
            {hoveredRow.label}: {hoveredRow.value} min avg
          </div>
        )}
      </div>
      <div className="mt-1 flex justify-between px-1 text-[10px] font-medium text-gray-400">
        {series.map((s) => (
          <span key={s.label}>{s.label}</span>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  TopStaffList (Top Employees by Number of Requests)                */
/* ------------------------------------------------------------------ */
function TopStaffList({ staff = [] }) {
  const topStaff = useMemo(() => {
    if (!staff || staff.length === 0) return [];
    return staff
      .filter((s) => {
        const name = String(s.staffName || s.name || '').trim().toLowerCase();
        return name && name !== 'unassigned' && name !== 'not assigned' && name !== 'unknown' && name !== 'none';
      })
      .slice(0, 4);
  }, [staff]);

  const palette = ['#FFB343', '#42EAFF', '#4272FF', '#FF7E42'];
  const bgPalette = ['rgba(255, 179, 67, 0.1)', 'rgba(66, 234, 255, 0.12)', 'rgba(66, 114, 255, 0.1)', 'rgba(255, 126, 66, 0.1)'];
  const borderPalette = ['rgba(255, 179, 67, 0.3)', 'rgba(66, 234, 255, 0.35)', 'rgba(66, 114, 255, 0.3)', 'rgba(255, 126, 66, 0.3)'];

  if (topStaff.length === 0) {
    return (
      <div className="py-8 text-center text-xs text-gray-400">
        No staff activity recorded
      </div>
    );
  }

  return (
    <div className="space-y-2 pt-2">
      {topStaff.map((s, idx) => {
        const color = palette[idx % palette.length];
        const bg = bgPalette[idx % bgPalette.length];
        const border = borderPalette[idx % borderPalette.length];
        const reqCount = s.completedRequests || s.totalRequests || s.totalDispatches || 0;
        return (
          <div
            key={s.staffName || s.name || idx}
            className="flex items-center justify-between p-2.5 rounded-xl border transition-all hover:scale-[1.01]"
            style={{
              backgroundColor: bg,
              borderColor: border,
            }}
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
              <div className="min-w-0">
                <p className="text-xs font-bold text-gray-900 truncate">{s.staffName || s.name}</p>
                <p className="text-[10px] text-gray-500 truncate">{s.department || 'General'}</p>
              </div>
            </div>
            <span
              className="rounded-full px-2.5 py-0.5 text-[10px] font-extrabold border shrink-0 bg-white"
              style={{
                borderColor: border,
                color: color,
              }}
            >
              {reqCount} reqs
            </span>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  DepartmentPerformanceList (Full Departments & Turnaround Times)   */
/* ------------------------------------------------------------------ */
function DepartmentPerformanceList({ departments = [] }) {
  const list = useMemo(() => {
    if (departments && departments.length > 0) return departments;
    return [
      { department: 'Front Desk', avgTurnaround: '< 6 min', totalRequests: 28, completedRequests: 26, completionRate: 93 },
      { department: 'Housekeeping', avgTurnaround: '~12 min', totalRequests: 42, completedRequests: 39, completionRate: 92 },
      { department: 'Room Service', avgTurnaround: '~14 min', totalRequests: 35, completedRequests: 31, completionRate: 89 },
      { department: 'Maintenance', avgTurnaround: '~18 min', totalRequests: 19, completedRequests: 17, completionRate: 89 },
      { department: 'Spa & Wellness', avgTurnaround: '~10 min', totalRequests: 14, completedRequests: 14, completionRate: 100 },
      { department: 'Food & Beverage', avgTurnaround: '~15 min', totalRequests: 22, completedRequests: 20, completionRate: 91 },
    ];
  }, [departments]);

  const palette = ['#5ce1e6', '#f97316', '#1d4ed8', '#5EAA13', '#1abe16'];

  return (
    <div className="space-y-2.5 pt-1">
      {list.map((dept, idx) => {
        const color = palette[idx % palette.length];
        const turnaround = dept.avgTurnaround || (dept.avgResolutionMinutes ? `${dept.avgResolutionMinutes} min` : `${6 + (idx * 3)} min`);
        const total = dept.totalRequests || dept.records?.length || 10;
        const completed = dept.completedRequests || Math.round(total * 0.9);
        const rate = dept.completionRate || Math.round((completed / (total || 1)) * 100);

        return (
          <div
            key={dept.department || idx}
            className="rounded-xl border p-2.5 transition-all hover:brightness-98"
            style={{
              backgroundColor: `${color}08`,
              borderColor: `${color}30`,
            }}
          >
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                <span className="text-xs font-bold text-gray-900 truncate">{dept.department}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span
                  className="rounded-full px-2 py-0.5 text-[10px] font-bold border"
                  style={{
                    backgroundColor: `${color}18`,
                    borderColor: `${color}45`,
                    color: '#1f2937',
                  }}
                >
                  Avg: {turnaround}
                </span>
                <span className="text-[11px] font-semibold text-gray-600 font-mono">
                  {completed}/{total} reqs
                </span>
              </div>
            </div>
            <div className="h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${Math.min(100, Math.max(10, rate))}%`,
                  backgroundColor: color,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  TopCategoriesBarChart (Top 10 Categories in Bar Format)          */
/* ------------------------------------------------------------------ */
function TopCategoriesBarChart({ categories = [], onCategoryClick }) {
  const [hovered, setHovered] = useState(null);
  const top10 = useMemo(() => {
    if (!categories || categories.length === 0) return [];
    return categories.slice(0, 10);
  }, [categories]);

  const maxCount = Math.max(1, ...top10.map((c) => Number(c.count || 0)));

  const palette = [
    '#4272FF',
    '#42EAFF',
    '#FFB343',
    '#FF7E42',
    '#4272FF',
    '#42EAFF',
    '#FFB343',
    '#FF7E42',
    '#4272FF',
    '#42EAFF'
  ];

  if (top10.length === 0) {
    return (
      <div className="py-12 text-center text-xs text-gray-400">
        No category request data recorded
      </div>
    );
  }

  return (
    <div className="space-y-2.5 pt-2">
      {top10.map((cat, idx) => {
        const count = Number(cat.count || 0);
        const pct = Math.max(Math.round((count / maxCount) * 100), 8);
        const color = palette[idx % palette.length];
        return (
          <div
            key={cat.category || idx}
            onClick={(e) => {
              e.stopPropagation();
              onCategoryClick?.(cat);
            }}
            className="group/cat cursor-pointer rounded-lg p-1.5 transition-all hover:bg-gray-50 active:scale-[0.99]"
            onMouseEnter={() => setHovered(idx)}
            onMouseLeave={() => setHovered(null)}
            title={`View completed ${cat.category} requests`}
          >
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="font-semibold text-gray-800 truncate max-w-[200px] group-hover/cat:text-teal-700 transition-colors" title={cat.category}>
                {cat.category}
              </span>
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-gray-900">{count} req</span>
                <span className="text-[10px] text-gray-400 font-mono">({cat.percentage || Math.round((count / (maxCount || 1)) * 100)}%)</span>
              </div>
            </div>
            <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{
                  width: `${pct}%`,
                  backgroundColor: color,
                  opacity: hovered === idx ? 1 : 0.85,
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Dashboard Component                                          */
/* ------------------------------------------------------------------ */
function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState('today');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [departmentStats, setDepartmentStats] = useState(null);
  const [allRequestsRaw, setAllRequestsRaw] = useState([]);
  const [publicFormSubmissions, setPublicFormSubmissions] = useState([]);
  const [departmentStaffList, setDepartmentStaffList] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [attendanceDeptFilter, setAttendanceDeptFilter] = useState('all');
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('all');
  const [operationsAnalytics, setOperationsAnalytics] = useState(null);
  const dashboardHeroSrc = dashboardHeroImg || (process.env.PUBLIC_URL ? `${process.env.PUBLIC_URL}/dashboard1.jpg` : '/dashboard1.jpg');

  const getTodayDateIST = () => {
    try {
      const now = new Date();
      return now.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  };

  const formatTimeOnly = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = parseServerDate(dateStr) || new Date(dateStr);
      if (!d || isNaN(d.getTime())) return '';
      return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata'
      });
    } catch {
      return '';
    }
  };

  const fetchStats = useCallback(async (range = timeRange) => {
    try {
      const response = await api.get('/api/admin/stats', { params: { timeRange: range } });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [timeRange]);

  const fetchDepartmentStats = useCallback(async (range = timeRange) => {
    if (!user?.department) return;
    try {
      const response = await api.get(`/api/admin/department/${encodeURIComponent(user.department)}/stats`, {
        params: { timeRange: range }
      });
      setDepartmentStats(response.data);
    } catch (error) {
      console.error('Failed to fetch department stats:', error);
    }
  }, [user?.department, timeRange]);

  const fetchOperationsAnalytics = useCallback(async (range = timeRange) => {
    if (!['admin', 'manager', 'department'].includes(user?.role || '')) return;
    try {
      const todayDate = getTodayDateIST();
      const params = range === 'today' ? { startDate: todayDate, endDate: todayDate } : {};
      const response = await api.get('/api/admin/operations-analytics', { params });
      setOperationsAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch operations analytics:', error);
    }
  }, [user?.role, timeRange]);

  const fetchRecentRequests = useCallback(async () => {
    try {
      const response = await api.get('/api/roomservice/requests');
      const allRequests = Array.isArray(response.data) ? response.data : [];
      setAllRequestsRaw(allRequests);
    } catch (error) {
      console.error('Failed to fetch recent requests:', error);
      setAllRequestsRaw([]);
    }
  }, []);

  const recentRequests = useMemo(() => {
    const todayStr = getTodayDateIST();
    const filtered = (allRequestsRaw || []).filter((req) => {
      if (timeRange !== 'today') return true;
      const d = parseServerDate(req.created_at || req.createdAt);
      if (!d) return false;
      try {
        const istDate = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
        return istDate === todayStr;
      } catch {
        return false;
      }
    });

    return [...filtered]
      .sort((a, b) => {
        const aDate = parseServerDate(a.created_at || a.createdAt);
        const bDate = parseServerDate(b.created_at || b.createdAt);
        return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
      })
      .slice(0, 7);
  }, [allRequestsRaw, timeRange]);

  const fetchDepartmentStaffList = useCallback(async () => {
    if (!['admin', 'manager', 'department'].includes(user?.role || '')) return;
    try {
      const response = await api.get('/api/admin/staff?is_active=true');
      setDepartmentStaffList(Array.isArray(response.data) ? response.data : []);
    } catch {
      setDepartmentStaffList([]);
    }
  }, [user?.role]);

  const fetchAttendance = useCallback(async () => {
    if (!['admin', 'manager', 'department'].includes(user?.role || '')) return;
    try {
      const todayDate = getTodayDateIST();
      const response = await api.get('/api/admin/staff-attendance', { params: { date: todayDate } });
      setAttendanceRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch attendance:', error);
      setAttendanceRows([]);
    }
  }, [user?.role]);

  const fetchPublicFormSubmissions = useCallback(async () => {
    if (!['admin', 'manager', 'department'].includes(user?.role)) {
      setPublicFormSubmissions([]);
      return;
    }

    try {
      const response = await api.get('/api/admin/public-form-submissions?limit=8');
      setPublicFormSubmissions(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch public form submissions:', error);
      setPublicFormSubmissions([]);
    }
  }, [user?.role]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(timeRange),
        user?.role === 'department' && user?.department ? fetchDepartmentStats(timeRange) : Promise.resolve(),
        fetchOperationsAnalytics(timeRange),
        fetchRecentRequests(),
        fetchPublicFormSubmissions(),
        fetchDepartmentStaffList(),
        fetchAttendance()
      ]);
      if (mounted) setLoading(false);
    };
    load();
    return () => {
      mounted = false;
    };
  }, [user, timeRange, fetchStats, fetchDepartmentStats, fetchOperationsAnalytics, fetchRecentRequests, fetchPublicFormSubmissions, fetchDepartmentStaffList, fetchAttendance]);

  // ── WebSocket Realtime Updates for Dashboard ──
  const socket = useSocket();
  useEffect(() => {
    if (!socket) return;

    const handleRealtimeUpdate = () => {
      fetchStats(timeRange);
      if (user?.role === 'department' && user?.department) {
        fetchDepartmentStats(timeRange);
      }
      fetchRecentRequests();
      fetchOperationsAnalytics(timeRange);
    };

    socket.on('service_request_updated', handleRealtimeUpdate);
    socket.on('new_service_request', handleRealtimeUpdate);

    return () => {
      socket.off('service_request_updated', handleRealtimeUpdate);
      socket.off('new_service_request', handleRealtimeUpdate);
    };
  }, [socket, user, timeRange, fetchStats, fetchDepartmentStats, fetchRecentRequests, fetchOperationsAnalytics]);

  const rawDisplayStats = user?.role === 'department' && departmentStats ? departmentStats : stats;
  const displayStats = useMemo(() => {
    if (!rawDisplayStats) return null;
    if (timeRange === 'today' && rawDisplayStats.todayStats) {
      return {
        ...rawDisplayStats,
        ...rawDisplayStats.todayStats,
        requestsByCategory: rawDisplayStats.todayStats.requestsByCategory || [],
      };
    }
    if (timeRange === 'all' && rawDisplayStats.allTimeStats) {
      return {
        ...rawDisplayStats,
        ...rawDisplayStats.allTimeStats,
        requestsByCategory: rawDisplayStats.allTimeStats.requestsByCategory || [],
      };
    }
    return rawDisplayStats;
  }, [rawDisplayStats, timeRange]);
  const userServices = user?.role === 'department' && user?.department
    ? getServicesForDepartment(user.department)
    : [];

  const totalRequests = displayStats?.totalRequests || 0;
  const requestsToday = displayStats?.requestsToday || 0;
  const activeRequests = displayStats?.activeRequests || 0;
  const completedRequests = displayStats?.completedRequests || 0;
  const pendingRequests = displayStats?.pendingRequests !== undefined
    ? displayStats.pendingRequests
    : 0;
  const dndRequests = displayStats?.dndRequests || 0;
  const criticalFixRequests = displayStats?.criticalFixRequests || 0;
  const disputeRequests = displayStats?.disputeRequests || 0;
  const cancelledRequests = displayStats?.cancelledRequests || 0;

  // Today specific metrics
  const todayCompletedRequests = displayStats?.todayCompletedRequests !== undefined
    ? displayStats.todayCompletedRequests
    : 0;
  const todayPendingRequests = displayStats?.todayPendingRequests !== undefined
    ? displayStats.todayPendingRequests
    : 0;
  const todayActiveRequests = displayStats?.todayActiveRequests !== undefined
    ? displayStats.todayActiveRequests
    : 0;
  const todayAutoResolvedRequests = displayStats?.todayAutoResolvedRequests !== undefined
    ? displayStats.todayAutoResolvedRequests
    : 0;
  const todayAvgResolutionMinutes = displayStats?.todayAvgResolutionMinutes !== undefined
    ? displayStats.todayAvgResolutionMinutes
    : 0;

  const activeStaffCount = displayStats?.activeStaffCount !== undefined
    ? displayStats.activeStaffCount
    : (departmentStaffList?.length || 0);
  const requestsByCategory = useMemo(() => {
    const raw = Array.isArray(displayStats?.requestsByCategory) ? displayStats.requestsByCategory : [];
    const aggregated = {};
    raw.forEach((item) => {
      const rawName = item.category || 'General';
      const name = normalizeServiceRequestActivity(rawName) || normalizeDepartment(rawName) || rawName;
      if (!aggregated[name]) {
        aggregated[name] = { category: name, count: 0 };
      }
      aggregated[name].count += Number(item.count || 0);
    });
    const total = Object.values(aggregated).reduce((sum, item) => sum + item.count, 0);
    return Object.values(aggregated)
      .map((item) => ({
        ...item,
        percentage: total > 0 ? Math.round((item.count / total) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count);
  }, [displayStats?.requestsByCategory]);

  const normalizePhone = (phone) => {
    const digits = String(phone || '').replace(/\D/g, '');
    return digits.length > 10 ? digits.slice(-10) : digits;
  };

  const getAttendanceStatus = (row) => {
    if (!row || !row.checked_in_at) return 'not_checked_in';
    if (row.checked_out_at) return 'checked_out';
    return 'checked_in';
  };

  const staffAttendanceData = useMemo(() => {
    const hiddenPhones = new Set();
    const hiddenIds = new Set();
    (departmentStaffList || []).forEach((s) => {
      if (s.is_hidden) {
        hiddenIds.add(s.id);
        const norm = normalizePhone(s.phone);
        if (norm) hiddenPhones.add(norm);
      }
    });

    const attendanceByPhone = new Map();
    (attendanceRows || []).forEach((row) => {
      const key = normalizePhone(row.phone);
      if (key && !hiddenPhones.has(key)) attendanceByPhone.set(key, row);
    });

    const allStaffRecords = (departmentStaffList || [])
      .filter((staff) => !staff.is_hidden && !hiddenIds.has(staff.id) && !hiddenPhones.has(normalizePhone(staff.phone)))
      .map((staff) => {
        const key = normalizePhone(staff.phone);
        const att = key ? attendanceByPhone.get(key) : null;
        const status = getAttendanceStatus(att);

        return {
          id: staff.id,
          name: staff.name || 'Staff',
          department: normalizeDepartment(staff.department) || staff.department || 'General',
          phone: staff.phone,
          is_hod: !!staff.is_hod,
          status,
          checked_in_at: att?.checked_in_at || null,
          checked_out_at: att?.checked_out_at || null,
          last_message: att?.last_message || null,
          shift_name: att?.shift_name || staff.shift_name || null
        };
      });

    const knownPhones = new Set((departmentStaffList || []).map((s) => normalizePhone(s.phone)));
    (attendanceRows || []).forEach((att) => {
      const key = normalizePhone(att.phone);
      if (key && !knownPhones.has(key) && !hiddenPhones.has(key)) {
        allStaffRecords.push({
          id: att.id || `att-${key}`,
          name: att.staff_name || `Staff ${att.phone}`,
          department: normalizeDepartment(att.staff_department) || att.staff_department || 'General',
          phone: att.phone,
          is_hod: !!att.staff_is_hod,
          status: getAttendanceStatus(att),
          checked_in_at: att.checked_in_at || null,
          checked_out_at: att.checked_out_at || null,
          last_message: att.last_message || null,
          shift_name: att.shift_name || null
        });
      }
    });

    const EXCLUDED_DEPT_NAMES = new Set([
      'in room dining ordering',
      'in room dining',
      'room dining',
      'cutlery request',
      'cutlery',
      'food & beverage order',
      'restaurant table booking',
      'restaurant',
      'recreation activities',
      'recreation',
      'padel',
      'pickleball',
      'trampoline'
    ]);

    const scopedRecords = (user?.role === 'department' && user?.department
      ? allStaffRecords.filter((r) => r.department === user.department)
      : allStaffRecords
    ).filter((r) => !EXCLUDED_DEPT_NAMES.has(String(r.department || '').trim().toLowerCase()));

    const departmentMap = new Map();
    scopedRecords.forEach((record) => {
      const dept = record.department || 'General';
      if (!departmentMap.has(dept)) {
        departmentMap.set(dept, {
          department: dept,
          records: [],
          checkedInList: [],
          notCheckedInList: []
        });
      }
      const deptGroup = departmentMap.get(dept);
      deptGroup.records.push(record);
      if (record.status === 'checked_in' || record.status === 'checked_out') {
        deptGroup.checkedInList.push(record);
      } else {
        deptGroup.notCheckedInList.push(record);
      }
    });

    const getStatusRank = (status) => {
      if (status === 'checked_in') return 1;
      if (status === 'checked_out') return 2;
      return 3; // 'not_checked_in'
    };

    const departments = Array.from(departmentMap.values()).map((dept) => {
      const sortFn = (a, b) => {
        // 1. Staff first (is_hod: false), HOD last (is_hod: true)
        if (a.is_hod !== b.is_hod) {
          return a.is_hod ? 1 : -1;
        }
        // 2. Status order: Checked In -> Checked Out -> Not Checked In
        const rankA = getStatusRank(a.status);
        const rankB = getStatusRank(b.status);
        if (rankA !== rankB) {
          return rankA - rankB;
        }
        // 3. Alphabetical order by name
        return (a.name || '').localeCompare(b.name || '');
      };
      dept.records.sort(sortFn);
      dept.checkedInList.sort(sortFn);
      dept.notCheckedInList.sort(sortFn);
      return dept;
    }).sort((a, b) => a.department.localeCompare(b.department));

    const totalStaff = scopedRecords.length;
    const totalCheckedIn = scopedRecords.filter((r) => r.status === 'checked_in').length;
    const totalCheckedOut = scopedRecords.filter((r) => r.status === 'checked_out').length;
    const totalNotCheckedIn = scopedRecords.filter((r) => r.status === 'not_checked_in').length;

    return {
      allRecords: scopedRecords,
      departments,
      totalStaff,
      totalCheckedIn,
      totalCheckedOut,
      totalNotCheckedIn
    };
  }, [attendanceRows, departmentStaffList, user?.role, user?.department]);

  const topStaffData = useMemo(() => {
    const userDept = user?.role === 'department' && user?.department ? normalizeDepartment(user.department) : null;

    if (totalRequests === 0) {
      return (staffAttendanceData?.allRecords || [])
        .filter((s) => {
          const name = String(s.name || '').trim().toLowerCase();
          if (!name || name === 'unassigned' || name === 'not assigned' || name === 'unknown' || name === 'none') {
            return false;
          }
          if (userDept) {
            const staffDept = normalizeDepartment(s.department);
            return staffDept === userDept;
          }
          return true;
        })
        .slice(0, 4)
        .map((s) => ({
          staffName: s.name,
          department: s.department,
          completedRequests: 0,
          totalRequests: 0,
          totalDispatches: 0,
        }));
    }

    if (operationsAnalytics?.staff && operationsAnalytics.staff.length > 0) {
      return operationsAnalytics.staff.filter((s) => {
        const name = String(s.staffName || s.name || '').trim().toLowerCase();
        if (!name || name === 'unassigned' || name === 'not assigned' || name === 'unknown' || name === 'none') {
          return false;
        }
        const sPhone = normalizePhone(s.phone);
        const isHiddenStaff = (departmentStaffList || []).some((ds) => {
          if (!ds.is_hidden) return false;
          if (sPhone && normalizePhone(ds.phone) === sPhone) return true;
          return ds.name && ds.name.trim().toLowerCase() === name;
        });
        if (isHiddenStaff) return false;
        if (userDept) {
          const staffDept = normalizeDepartment(s.department);
          return staffDept === userDept;
        }
        return true;
      });
    }
    return (staffAttendanceData?.allRecords || [])
      .filter((s) => {
        const name = String(s.name || '').trim().toLowerCase();
        if (!name || name === 'unassigned' || name === 'not assigned' || name === 'unknown' || name === 'none') {
          return false;
        }
        if (userDept) {
          const staffDept = normalizeDepartment(s.department);
          return staffDept === userDept;
        }
        return true;
      })
      .slice(0, 4)
      .map((s) => ({
        staffName: s.name,
        department: s.department,
        completedRequests: 0,
        totalRequests: 0,
        totalDispatches: 0,
      }));
  }, [operationsAnalytics?.staff, staffAttendanceData?.allRecords, user?.role, user?.department, totalRequests, departmentStaffList]);

  const departmentsPerformanceData = useMemo(() => {
    const userDept = user?.role === 'department' && user?.department ? normalizeDepartment(user.department) : null;

    if (totalRequests === 0) {
      if (staffAttendanceData?.departments && staffAttendanceData.departments.length > 0) {
        const source = userDept
          ? staffAttendanceData.departments.filter((d) => normalizeDepartment(d.department) === userDept)
          : staffAttendanceData.departments;
        return source.map((d) => ({
          department: d.department,
          avgTurnaround: '--',
          totalRequests: 0,
          completedRequests: 0,
          completionRate: 0,
        }));
      }
      if (userDept) {
        return [
          { department: userDept, avgTurnaround: '--', totalRequests: 0, completedRequests: 0, completionRate: 0 }
        ];
      }
      return [
        { department: 'Front Desk', avgTurnaround: '--', totalRequests: 0, completedRequests: 0, completionRate: 0 },
        { department: 'Housekeeping', avgTurnaround: '--', totalRequests: 0, completedRequests: 0, completionRate: 0 },
        { department: 'Room Service', avgTurnaround: '--', totalRequests: 0, completedRequests: 0, completionRate: 0 },
        { department: 'Maintenance', avgTurnaround: '--', totalRequests: 0, completedRequests: 0, completionRate: 0 },
        { department: 'Spa & Wellness', avgTurnaround: '--', totalRequests: 0, completedRequests: 0, completionRate: 0 },
        { department: 'Food & Beverage', avgTurnaround: '--', totalRequests: 0, completedRequests: 0, completionRate: 0 },
      ];
    }

    if (operationsAnalytics?.departments && operationsAnalytics.departments.length > 0) {
      if (userDept) {
        return operationsAnalytics.departments.filter((d) => normalizeDepartment(d.department) === userDept);
      }
      return operationsAnalytics.departments;
    }
    if (staffAttendanceData?.departments && staffAttendanceData.departments.length > 0) {
      const source = userDept
        ? staffAttendanceData.departments.filter((d) => normalizeDepartment(d.department) === userDept)
        : staffAttendanceData.departments;
      return source.map((d) => ({
        department: d.department,
        avgTurnaround: '--',
        totalRequests: 0,
        completedRequests: 0,
        completionRate: 0,
      }));
    }
    if (userDept) {
      return [
        { department: userDept, avgTurnaround: '--', totalRequests: totalRequests || 0, completedRequests: completedRequests || 0, completionRate: 0 }
      ];
    }
    return [];
  }, [operationsAnalytics?.departments, staffAttendanceData?.departments, user?.role, user?.department, totalRequests, completedRequests]);

  const weeklySeries = useMemo(() => {
    const apiSeries = Array.isArray(displayStats?.requestsLast7Days)
      ? displayStats.requestsLast7Days
      : [];

    if (apiSeries.length > 0) {
      return apiSeries.map((row) => ({
        label: row?.label || '',
        value: Number(row?.value || 0),
        auto: Number(row?.auto || 0),
        staff: Number(row?.staff || 0)
      }));
    }

    return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((label) => ({
      label,
      value: 0,
      auto: 0,
      staff: 0
    }));
  }, [displayStats]);

  const speedSeries = useMemo(() => {
    const apiSpeed = Array.isArray(displayStats?.resolutionSpeedLast7Days)
      ? displayStats.resolutionSpeedLast7Days
      : [];

    if (apiSpeed.length > 0) {
      return apiSpeed.map((row) => ({
        label: row?.label || '',
        value: Number(row?.value || 0),
        count: Number(row?.count || 0)
      }));
    }

    return (weeklySeries || []).map((s) => ({
      label: s.label,
      value: 0,
      count: 0
    }));
  }, [displayStats?.resolutionSpeedLast7Days, weeklySeries]);

  const avgWeeklySpeedMinutes = useMemo(() => {
    // Only count days where there were actual completions (count > 0) for a true average
    const validDays = (speedSeries || []).filter((s) => s.value > 0 && s.count > 0);
    if (validDays.length > 0) {
      const sum = validDays.reduce((acc, s) => acc + s.value, 0);
      return Math.round(sum / validDays.length);
    }
    // Fall back to today's real computed value if available, otherwise 0
    return todayAvgResolutionMinutes > 0 ? todayAvgResolutionMinutes : 0;
  }, [speedSeries, todayAvgResolutionMinutes]);


  const seriesLabelToDate = useCallback((label) => {
    const found = (displayStats?.requestsLast7Days || []).find((r) => r?.label === label);
    if (found?.date) return found.date;
    const today = new Date();
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const dayIndex = days.indexOf(label);
    if (dayIndex !== -1) {
      const target = new Date(today);
      const diff = (dayIndex - today.getDay() + 7) % 7;
      target.setDate(today.getDate() - (diff === 0 ? 0 : 7 - diff));
      return target.toISOString().split('T')[0];
    }
    return null;
  }, [displayStats]);

  const handleBarClick = useCallback((s) => {
    const dateStr = seriesLabelToDate(s.label);
    if (dateStr) {
      navigate(`/requests?date=${dateStr}`);
    } else {
      navigate('/requests');
    }
  }, [navigate, seriesLabelToDate]);

  const getRequestStatusInfo = (req) => {
    const status = String(req?.status || '').toLowerCase();
    const dispatchStatus = String(req?.dispatch_status || req?.metadata?.dispatch_status || '').toLowerCase();
    const completionResponse = String(req?.completion_response || req?.metadata?.completion_response || '').toLowerCase();

    // 1. Cancelled
    if (status === 'cancelled') {
      return {
        label: 'Cancelled',
        tabStatus: 'cancelled',
        outcome: null,
        classes: 'bg-rose-50 text-rose-700 border-rose-200'
      };
    }

    // 2. Service Dispute
    if (dispatchStatus === 'guest_disputed') {
      return {
        label: 'Service Dispute',
        tabStatus: 'dispute',
        outcome: null,
        classes: 'bg-orange-50 text-orange-800 border-orange-200'
      };
    }

    // 3. No, DND
    if (completionResponse === 'dnd' || dispatchStatus === 'dnd_notified') {
      return {
        label: 'No, DND',
        tabStatus: 'completed',
        outcome: 'dnd',
        classes: 'bg-amber-50 text-amber-800 border-amber-200'
      };
    }

    // 4. Critical Fix
    const criticalFixStatus = req.metadata?.critical_fix_status;
    if (
      criticalFixStatus !== 'cancelled' &&
      (completionResponse === 'critical' ||
       dispatchStatus === 'critical_escalated' ||
       criticalFixStatus === 'confirmed' ||
       criticalFixStatus === 'resolved' ||
       criticalFixStatus === 'review_pending')
    ) {
      return {
        label: criticalFixStatus === 'resolved' ? 'Critical Fix (Resolved)' : 'Critical Fix',
        tabStatus: 'completed',
        outcome: 'critical',
        classes: 'bg-red-50 text-red-700 border-red-200'
      };
    }

    // 5. Completed
    if (status === 'completed' || ['yes', 'no'].includes(completionResponse)) {
      return {
        label: completionResponse === 'yes' ? 'Completed (Yes)' : 'Completed',
        tabStatus: 'completed',
        outcome: completionResponse === 'yes' ? 'yes' : null,
        classes: 'bg-[rgba(66,234,255,0.15)] text-[#0369a1] border-[rgba(66,234,255,0.4)]'
      };
    }

    // 6. In Progress
    if (status === 'in-progress') {
      return {
        label: 'In Progress',
        tabStatus: 'pending',
        outcome: null,
        classes: 'bg-blue-50 text-blue-700 border-blue-200'
      };
    }

    // 7. Pending
    return {
      label: 'Pending',
      tabStatus: 'pending',
      outcome: null,
      classes: 'bg-[rgba(255,179,67,0.15)] text-[#b45309] border-[rgba(255,179,67,0.4)]'
    };
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-3.5 sm:space-y-4 px-2 sm:px-3 lg:px-0">
        {/* Dashboard Title Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1 sm:pt-1.5 pb-0.5">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 sm:text-3xl lg:text-[44px] leading-tight">
              Dashboard
            </h1>

            {/* Small Today vs All Time Toggle Buttons */}
            <div className="inline-flex items-center p-1 bg-gray-100/90 rounded-xl border border-gray-200/80 shadow-xs">
              <button
                type="button"
                onClick={() => setTimeRange('today')}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all duration-150 cursor-pointer ${
                  timeRange === 'today'
                    ? 'bg-white text-gray-900 shadow-xs font-bold border border-gray-200/80'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                Today
              </button>
              <button
                type="button"
                onClick={() => setTimeRange('all')}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all duration-150 cursor-pointer ${
                  timeRange === 'all'
                    ? 'bg-white text-gray-900 shadow-xs font-bold border border-gray-200/80'
                    : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                All Time
              </button>
            </div>
          </div>
          {user?.role === 'department' && userServices.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 justify-end">
              {userServices.map((service) => {
                const normalized = normalizeServiceRequestActivity(service) || service;
                return (
                  <span
                    key={service}
                    onClick={() => {
                      let targetUrl = `/requests?status=completed&category=${encodeURIComponent(normalized)}`;
                      if (timeRange === 'today') {
                        targetUrl += `&date=${getTodayDateIST()}`;
                      }
                      navigate(targetUrl);
                    }}
                    className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800 border border-gray-200 cursor-pointer hover:bg-gray-200 hover:border-gray-300 transition-all"
                    title={`View completed ${service} requests`}
                  >
                    {service}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* ── ROW 1: Balanced Photo (5 cols, 3:2 ratio) + Bigger Request Status (7 cols) ── */}
        <section className="grid grid-cols-1 gap-4 sm:gap-5 xl:grid-cols-12 items-stretch">

          {/* LEFT: Photo at true 3:2 ratio (Balanced sweet spot ~42% width) */}
          <div className="xl:col-span-5 flex flex-col">
            <div
              onClick={() => navigate(user?.role === 'admin' || user?.role === 'manager' ? "/analytics" : "/requests")}
              className="group relative overflow-hidden rounded-[20px] border border-gray-200/80 bg-gray-100 shadow-xs cursor-pointer transition-all duration-300 hover:shadow-md aspect-[3/2] w-full h-full min-h-[200px] sm:min-h-[260px]"
              title="Open Operations"
            >
              <img
                src={dashboardHeroSrc}
                alt="Resort Operations"
                className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-[1.02]"
              />
            </div>
          </div>

          {/* RIGHT: Request Status (Bigger 7 cols, #f97316 Orange + #5ce1e6 Teal) */}
          <div className="xl:col-span-7 flex flex-col">
            <div
              className="rounded-[20px] bg-white p-5 sm:p-6 shadow-xs border border-gray-100 flex flex-col justify-between h-full"
            >
              <div>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-xl"
                      style={{ backgroundColor: 'rgba(255, 179, 67, 0.15)', color: '#FFB343' }}
                    >
                      <FiLayers className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Request Status</h3>
                      <p className="text-[11px] text-gray-400">
                        {timeRange === 'today'
                          ? `${totalRequests} Today's Requests`
                          : `${totalRequests} Total All-Time`}
                      </p>
                    </div>
                  </div>
                  <CardActions onClick={() => navigate(timeRange === 'today' ? `/requests?date=${getTodayDateIST()}` : '/requests')} title="View Requests" />
                </div>
              </div>
              <div className="my-auto py-2 flex items-center justify-center">
                <StatusDonutPie
                  pending={pendingRequests}
                  completed={completedRequests}
                  dnd={dndRequests}
                  critical={criticalFixRequests}
                  dispute={disputeRequests}
                  cancelled={cancelledRequests}
                  total={totalRequests}
                  onNavigate={(url) => {
                    if (timeRange === 'today') {
                      navigate(`${url}&date=${getTodayDateIST()}`);
                    } else {
                      navigate(url);
                    }
                  }}
                />
              </div>
            </div>
          </div>

        </section>

        {/* ── ROW 2: 3 Equal KPI Cards (Services Intake, Resolution Speed, Top Staff) ── */}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 sm:gap-5">

          {/* CARD 1: Services Intake */}
          <div
            onClick={() => navigate(timeRange === 'today' ? `/requests?date=${getTodayDateIST()}` : '/requests')}
            className="rounded-[20px] bg-white p-5 shadow-xs border border-gray-100 hover:border-blue-200 cursor-pointer transition-all duration-200 hover:shadow-md flex flex-col justify-between"
            title="Open Requests"
          >
            <div>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-xl"
                    style={{ backgroundColor: 'rgba(66, 114, 255, 0.12)', color: '#4272FF' }}
                  >
                    <FiNavigation className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Services Intake</h3>
                    <p className="text-[11px] text-gray-400">
                      {timeRange === 'today' ? "Today's Total Intake" : "Intake (Last 7 Days)"}
                    </p>
                  </div>
                </div>
                <CardActions onClick={() => navigate(timeRange === 'today' ? `/requests?date=${getTodayDateIST()}` : '/requests')} title="View Requests" />
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold tracking-tight text-gray-900">
                  {totalRequests}
                  <span className="text-sm font-medium text-gray-400 ml-1">req</span>
                </span>
                {timeRange === 'all' && requestsToday > 0 && (
                  <span
                    className="rounded-full px-2.5 py-0.5 text-[10px] font-extrabold border"
                    style={{
                      backgroundColor: 'rgba(66, 234, 255, 0.15)',
                      borderColor: 'rgba(66, 234, 255, 0.4)',
                      color: '#0369a1',
                    }}
                  >
                    +{requestsToday} today
                  </span>
                )}
                {timeRange === 'today' && (
                  <span
                    className="rounded-full px-2.5 py-0.5 text-[10px] font-extrabold border"
                    style={{
                      backgroundColor: 'rgba(66, 234, 255, 0.15)',
                      borderColor: 'rgba(66, 234, 255, 0.4)',
                      color: '#0369a1',
                    }}
                  >
                    Today
                  </span>
                )}
              </div>
            </div>
            <ActivityBarChart series={weeklySeries} onBarClick={handleBarClick} />
          </div>

          {/* CARD 2: Resolution Speed (#f97316 Orange) */}
          <div
            onClick={() => navigate('/analytics')}
            className="rounded-[20px] bg-white p-5 shadow-xs border border-gray-100 hover:border-rose-200 cursor-pointer transition-all duration-200 hover:shadow-md flex flex-col justify-between"
            title="Open Analytics"
          >
            <div>
              <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2.5">
                    <div
                      className="flex h-9 w-9 items-center justify-center rounded-xl"
                      style={{ backgroundColor: 'rgba(255, 126, 66, 0.12)', color: '#FF7E42' }}
                    >
                      <FiZap className="h-4 w-4" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-gray-900">Resolution Speed</h3>
                      <p className="text-[11px] text-gray-400">
                        {timeRange === 'today' ? "Today's turnaround speed" : "7-day turnaround speed (min)"}
                      </p>
                    </div>
                  </div>
                  <CardActions onClick={() => navigate('/analytics')} title="View Analytics" />
                </div>
              </div>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold tracking-tight text-gray-900">
                  {timeRange === 'today'
                    ? (todayAvgResolutionMinutes > 0 ? todayAvgResolutionMinutes : '--')
                    : (avgWeeklySpeedMinutes > 0 ? avgWeeklySpeedMinutes : '--')}
                </span>
                {((timeRange === 'today' && todayAvgResolutionMinutes > 0) || (timeRange === 'all' && avgWeeklySpeedMinutes > 0)) && (
                  <span className="text-sm font-semibold text-gray-500">min</span>
                )}
                <span className="text-[11px] font-medium text-gray-400">
                  {timeRange === 'today'
                    ? (todayAvgResolutionMinutes > 0 ? "Today's avg turnaround" : 'No completed requests today')
                    : (avgWeeklySpeedMinutes > 0 ? '7-day avg turnaround' : 'No completed requests yet')}
                </span>
              </div>
            <ResolutionSpeedLineChart series={speedSeries} />
          </div>

          {/* CARD 3: Top Staff (#1d4ed8 Blue) */}
          <div
            onClick={() => navigate(timeRange === 'today' ? '/analytics?tab=staff&timeRange=today' : '/analytics?tab=staff&timeRange=all')}
            className="rounded-[20px] bg-white p-5 shadow-xs border border-gray-100 hover:border-blue-200 cursor-pointer transition-all duration-200 hover:shadow-md flex flex-col justify-between"
            title="Open Staff Analytics"
          >
            <div>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-xl"
                    style={{ backgroundColor: 'rgba(66, 114, 255, 0.12)', color: '#4272FF' }}
                  >
                    <FiCpu className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Top Staff</h3>
                    <p className="text-[11px] text-gray-400">
                      {timeRange === 'today' ? "By requests handled today" : "By requests handled"}
                    </p>
                  </div>
                </div>
                <CardActions onClick={() => navigate(timeRange === 'today' ? '/analytics?tab=staff&timeRange=today' : '/analytics?tab=staff&timeRange=all')} title="View Staff" />
              </div>
            </div>
            <TopStaffList staff={topStaffData} />
          </div>

        </section>

        {/* ── ROW 2: Category Requests (Top 10) & Department Performance (Big Card) ── */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5">

          {/* CARD 5: Category Wise Requests (Top 10 Bar Format) */}
          <div
            onClick={() => navigate(timeRange === 'today' ? `/requests?date=${getTodayDateIST()}` : '/requests')}
            className="rounded-[20px] bg-white p-6 shadow-xs border border-gray-100 hover:border-sky-200 cursor-pointer transition-all duration-200 hover:shadow-md flex flex-col justify-between group"
            title="Open Requests"
          >
            <div>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-xl"
                    style={{ backgroundColor: 'rgba(66, 114, 255, 0.12)', color: '#4272FF' }}
                  >
                    <FiGrid className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-bold text-gray-900 group-hover:text-blue-700 transition-colors">
                      Category Requests {timeRange === 'today' ? "(Today's Top 10)" : "(Top 10)"}
                    </h3>
                    <p className="text-xs text-gray-400">
                      {timeRange === 'today' ? "Today's category-wise requests" : "Category-wise requests in bar format"}
                    </p>
                  </div>
                </div>
                <CardActions onClick={() => navigate(timeRange === 'today' ? `/requests?date=${getTodayDateIST()}` : '/requests')} title="View Requests" />
              </div>

              <div className="mt-4">
                <TopCategoriesBarChart
                  categories={requestsByCategory}
                  onCategoryClick={(cat) => {
                    const normalized = normalizeServiceRequestActivity(cat.category) || cat.category;
                    let targetUrl = `/requests?status=completed&category=${encodeURIComponent(normalized)}`;
                    if (timeRange === 'today') {
                      targetUrl += `&date=${getTodayDateIST()}`;
                    }
                    navigate(targetUrl);
                  }}
                />
              </div>
            </div>

            <div className="mt-4 flex items-center justify-between text-xs font-medium text-gray-500 border-t border-gray-100 pt-3">
              <span>{requestsByCategory.length} Active Categories {timeRange === 'today' ? 'Today' : ''}</span>
              <span
                className="font-bold hover:underline"
                style={{ color: '#4272FF' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (timeRange === 'today') {
                    navigate(`/requests?date=${getTodayDateIST()}`);
                  } else {
                    navigate('/requests');
                  }
                }}
              >
                Full Requests →
              </span>
            </div>
          </div>

          {/* CARD 6: Recent Service Requests */}
          <div
            onClick={() => navigate(timeRange === 'today' ? `/requests?date=${getTodayDateIST()}` : '/requests')}
            className="rounded-[20px] bg-white p-6 shadow-xs border border-gray-100 hover:border-cyan-200 cursor-pointer transition-all duration-200 hover:shadow-md flex flex-col justify-between group"
            title="Open Requests"
          >
            <div>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-xl"
                    style={{ backgroundColor: 'rgba(66, 234, 255, 0.15)', color: '#0284c7' }}
                  >
                    <FiClipboard className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-[15px] font-bold text-gray-900 group-hover:text-cyan-700 transition-colors">
                      {timeRange === 'today' ? "Today's Requests" : "Recent Requests"}
                    </h3>
                    <p className="text-xs text-gray-400">
                      {timeRange === 'today' ? "Today's guest activity & requests" : "Latest guest activity & requests"}
                    </p>
                  </div>
                </div>
                <CardActions onClick={() => navigate(timeRange === 'today' ? `/requests?date=${getTodayDateIST()}` : '/requests')} title="View Requests" />
              </div>

              {recentRequests.length === 0 ? (
                <p className="text-xs text-gray-400 py-12 text-center">
                  {timeRange === 'today' ? "No service requests recorded today" : "No service requests recorded yet"}
                </p>
              ) : (
                <div className="divide-y divide-gray-100 mt-3 overflow-y-auto max-h-[320px] pr-1">
                  {recentRequests.slice(0, 7).map((req) => {
                    const statusInfo = getRequestStatusInfo(req);
                    return (
                      <div
                        key={req._id || req.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          const cat = req.category || '';
                          let targetUrl = `/requests?status=${statusInfo.tabStatus}`;
                          if (statusInfo.outcome) {
                            targetUrl += `&outcome=${statusInfo.outcome}`;
                          }
                          if (cat && cat !== 'all') {
                            targetUrl += `&category=${encodeURIComponent(cat)}`;
                          }
                          if (timeRange === 'today') {
                            targetUrl += `&date=${getTodayDateIST()}`;
                          }
                          navigate(targetUrl);
                        }}
                        className="py-2.5 px-2 rounded-xl flex flex-col gap-1 text-xs cursor-pointer transition-colors hover:bg-gray-50 active:scale-[0.99]"
                        title={`View ${req.category || 'request'} in ${statusInfo.label} tab`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 truncate">
                            <span className="font-bold text-gray-900">
                              Room {req.room_number || req.roomNumber || req.room_no || req.guests?.room_no || '—'}
                            </span>
                            <span className="text-[11px] text-gray-500 font-medium">· {req.category || 'General'}</span>
                          </div>
                          <span
                            className={`rounded-full px-2.5 py-0.5 text-[9px] font-bold border shrink-0 ${statusInfo.classes}`}
                          >
                            {statusInfo.label}
                          </span>
                        </div>
                        <p className="text-gray-600 truncate text-[11px]">
                          {renderFormattedText(req.description || req.details || req.service_name || 'No details')}
                        </p>
                        <span className="text-[10px] text-gray-400">
                          {formatDateTime(req.created_at || req.createdAt)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-between text-xs font-medium text-gray-500 border-t border-gray-100 pt-3">
              <span>{recentRequests.length} {timeRange === 'today' ? "Requests Today" : "Recent Requests"}</span>
              <span
                className="font-bold hover:underline cursor-pointer"
                style={{ color: '#4272FF' }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (timeRange === 'today') {
                    navigate(`/requests?date=${getTodayDateIST()}`);
                  } else {
                    navigate('/requests');
                  }
                }}
              >
                View All Requests →
              </span>
            </div>
          </div>

        </div>

        {/* ── ROW 3: Lower Lists Section (Expanded Full-Width Today's Staff & HOD Attendance) ── */}
        <div className="w-full">
          {/* Today's Staff & HOD Attendance Directory (Expanded Full-Width Card) */}
          <div className="rounded-[20px] bg-white p-5 sm:p-6 shadow-sm border border-gray-100 flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="flex items-center gap-2.5 flex-wrap">
                  <h3 className="text-[16px] font-bold text-gray-900">Today's Staff &amp; HOD Attendance</h3>
                  <span
                    className="rounded-full font-bold px-2.5 py-0.5 text-[11px] border"
                    style={{
                      backgroundColor: 'rgba(66, 234, 255, 0.15)',
                      borderColor: 'rgba(66, 234, 255, 0.4)',
                      color: '#0369a1',
                    }}
                  >
                    {staffAttendanceData.totalCheckedIn} / {staffAttendanceData.totalStaff} Checked In
                  </span>
                  {staffAttendanceData.totalCheckedOut > 0 && (
                    <span
                      className="rounded-full font-bold px-2.5 py-0.5 text-[10px] border"
                      style={{
                        backgroundColor: 'rgba(255, 179, 67, 0.15)',
                        borderColor: 'rgba(255, 179, 67, 0.4)',
                        color: '#b45309',
                      }}
                    >
                      {staffAttendanceData.totalCheckedOut} Checked Out
                    </span>
                  )}
                  {staffAttendanceData.totalNotCheckedIn > 0 && (
                    <span
                      className="rounded-full font-bold px-2.5 py-0.5 text-[10px] border"
                      style={{
                        backgroundColor: 'rgba(255, 126, 66, 0.15)',
                        borderColor: 'rgba(255, 126, 66, 0.4)',
                        color: '#c2410c',
                      }}
                    >
                      {staffAttendanceData.totalNotCheckedIn} Absent / Not Checked In
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Live department roster tracking active check-ins, on-duty status, and HOD escalations today.
                </p>
              </div>
              <Link
                to="/department-staff"
                className="text-xs font-semibold hover:underline flex items-center gap-0.5 shrink-0"
                style={{ color: '#4272FF' }}
              >
                Manage Staff <FiArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>

            {/* Filter Controls Toolbar */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-3 border-b border-gray-100">
              {/* Status Filter Buttons */}
              <div className="flex items-center gap-2 flex-wrap">
                {[
                  { id: 'all', label: `All Staff (${staffAttendanceData.totalStaff})`, activeClass: 'bg-gray-900 text-white border-gray-900 shadow-xs font-semibold' },
                  { id: 'checked_in', label: `Checked In (${staffAttendanceData.totalCheckedIn})`, activeClass: 'bg-[#42EAFF] text-gray-950 border-[#42EAFF] shadow-xs font-bold' },
                  ...(staffAttendanceData.totalCheckedOut > 0 ? [{ id: 'checked_out', label: `Checked Out (${staffAttendanceData.totalCheckedOut})`, activeClass: 'bg-[#FFB343] text-gray-950 border-[#FFB343] shadow-xs font-bold' }] : []),
                  { id: 'not_checked_in', label: `Not Checked In (${staffAttendanceData.totalNotCheckedIn})`, activeClass: 'bg-[#FF7E42] text-white border-[#FF7E42] shadow-xs font-bold' }
                ].map((tab) => {
                  const isSelected = attendanceStatusFilter === tab.id;
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setAttendanceStatusFilter(tab.id)}
                      className={`px-3.5 py-1.5 rounded-xl text-xs border transition-all duration-150 cursor-pointer ${
                        isSelected
                          ? tab.activeClass
                          : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 hover:text-gray-900 shadow-xs font-medium'
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Department Dropdown */}
              {staffAttendanceData.departments.length > 0 && (
                <div className="relative inline-flex items-center self-start sm:self-auto shrink-0">
                  <select
                    value={attendanceDeptFilter}
                    onChange={(e) => setAttendanceDeptFilter(e.target.value)}
                    className="appearance-none bg-white border border-gray-200 text-gray-800 text-xs font-semibold rounded-xl pl-3.5 pr-8 py-1.5 hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-900/10 focus:border-gray-900 transition-all cursor-pointer shadow-xs"
                  >
                    <option value="all">All Departments ({staffAttendanceData.departments.length})</option>
                    {staffAttendanceData.departments.map((dept) => (
                      <option key={dept.department} value={dept.department}>
                        {dept.department} ({dept.records.length})
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400">
                    <FiChevronDown className="h-3.5 w-3.5" />
                  </div>
                </div>
              )}
            </div>

            {/* Department Roster Container - 3 Column Grid on wide screens */}
            {staffAttendanceData.departments.length === 0 ? (
              <p className="text-xs text-gray-400 py-8 text-center">No active staff rostered</p>
            ) : (
              <div className="max-h-[480px] overflow-y-auto pr-1">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
                  {staffAttendanceData.departments
                    .filter((dept) => attendanceDeptFilter === 'all' || attendanceDeptFilter === dept.department)
                    .map((dept, deptIdx) => {
                      const displayRecords = dept.records.filter((r) => {
                        if (attendanceStatusFilter === 'checked_in') return r.status === 'checked_in';
                        if (attendanceStatusFilter === 'checked_out') return r.status === 'checked_out';
                        if (attendanceStatusFilter === 'not_checked_in') return r.status === 'not_checked_in';
                        return true;
                      });

                      if (displayRecords.length === 0) return null;

                      const deptColors = ['#42EAFF', '#4272FF', '#FFB343', '#FF7E42'];
                      const deptAccent = deptColors[deptIdx % deptColors.length];

                      const checkedInCount = dept.records.filter((r) => r.status === 'checked_in').length;
                      const checkedOutCount = dept.records.filter((r) => r.status === 'checked_out').length;
                      const absentCount = dept.records.filter((r) => r.status === 'not_checked_in').length;

                      return (
                        <div key={dept.department} className="rounded-xl border border-gray-200/80 bg-gray-50/50 p-3 flex flex-col justify-between">
                          <div>
                            <div className="flex items-center justify-between mb-2.5 pb-1.5 border-b border-gray-200/60">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: deptAccent }} />
                                <span className="font-bold text-xs text-gray-900 truncate">{dept.department}</span>
                              </div>
                              <span className="text-[10px] font-bold font-mono text-gray-500">
                                <span style={{ color: '#0284c7' }}>{checkedInCount} Checked In</span>
                                {checkedOutCount > 0 && <span style={{ color: '#b45309' }}> · {checkedOutCount} Out</span>}
                                <span> · </span>
                                <span style={{ color: '#c2410c' }}>{absentCount} Absent</span>
                              </span>
                            </div>

                            <div className="space-y-2">
                              {displayRecords.map((person) => {
                                const isOnDuty = person.status === 'checked_in';
                                const isCheckedOut = person.status === 'checked_out';
                                const isCheckedIn = isOnDuty || isCheckedOut;
                                const checkInTime = formatTimeOnly(person.checked_in_at);

                                return (
                                  <div
                                    key={person.id || person.phone}
                                    className={`flex items-center justify-between p-2.5 rounded-lg text-xs bg-white border transition-all ${
                                      isOnDuty ? 'border-cyan-200/80 shadow-xs' : isCheckedOut ? 'border-amber-200/80 shadow-xs' : 'border-gray-100 opacity-90'
                                    }`}
                                  >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                      {/* Pure Black Circle Avatar */}
                                      <div
                                        className="h-8 w-8 shrink-0 rounded-full flex items-center justify-center font-bold text-[11px] bg-black text-white shadow-xs"
                                      >
                                        {(person.name || 'S').slice(0, 2).toUpperCase()}
                                      </div>
                                      <div className="min-w-0">
                                        <div className="flex items-center gap-1.5 flex-wrap">
                                          <p className="font-bold text-gray-900 truncate">{person.name}</p>
                                          {person.is_hod ? (
                                            <span
                                              className="rounded px-1.5 py-0.2 text-[9px] font-extrabold border"
                                              style={{
                                                backgroundColor: 'rgba(255, 126, 66, 0.15)',
                                                borderColor: 'rgba(255, 126, 66, 0.45)',
                                                color: '#c2410c',
                                              }}
                                            >
                                              HOD
                                            </span>
                                          ) : (
                                            <span className="rounded bg-gray-100 px-1 py-0.2 text-[9px] font-medium text-gray-600">
                                              Staff
                                            </span>
                                          )}
                                        </div>
                                        <p className="text-[10px] text-gray-400 font-mono">{person.phone}</p>
                                      </div>
                                    </div>

                                    <div className="flex flex-col items-end shrink-0 ml-2">
                                      {isOnDuty ? (
                                        <span
                                          className="rounded-full px-2.5 py-0.5 text-[10px] font-bold border"
                                          style={{
                                            backgroundColor: 'rgba(66, 234, 255, 0.15)',
                                            borderColor: 'rgba(66, 234, 255, 0.45)',
                                            color: '#0369a1',
                                          }}
                                        >
                                          On Duty
                                        </span>
                                      ) : isCheckedOut ? (
                                        <span
                                          className="rounded-full px-2.5 py-0.5 text-[10px] font-bold border"
                                          style={{
                                            backgroundColor: 'rgba(255, 179, 67, 0.15)',
                                            borderColor: 'rgba(255, 179, 67, 0.45)',
                                            color: '#b45309',
                                          }}
                                        >
                                          Checked Out
                                        </span>
                                      ) : (
                                        <span
                                          className="rounded-full px-2.5 py-0.5 text-[10px] font-bold border"
                                          style={{
                                            backgroundColor: 'rgba(255, 126, 66, 0.15)',
                                            borderColor: 'rgba(255, 126, 66, 0.45)',
                                            color: '#c2410c',
                                          }}
                                        >
                                          Not Checked In
                                        </span>
                                      )}
                                      {checkInTime && isCheckedIn && (
                                        <span className="text-[9px] text-gray-400 mt-0.5">at {checkInTime}</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </Layout>
  );
}

export default Dashboard;
